import { Play } from '../../spinnaxBase/logic/types_storage';
import { RankingResponse } from './interfaces/RankingInterfaces';
import { getAuth } from '../../util';
import { RANKER_URL } from 'util/constants';
import { logger } from 'services/Logger';

export class RankerService {
  private isInitialized = false;

  constructor() {
    this.runInitTest();
  }

  /**
   * Calculate score with backend service only - NO LOCAL FALLBACK
   * @param play - The play data to score
   * @returns Promise with ranking response from backend
   */
  async calculateScore(play: Play): Promise<RankingResponse> {
    try {
      const auth = getAuth();
      const user = auth.currentUser;

      // ✅ GEÄNDERT: Kein Local Fallback bei fehlendem User
      if (!user) {
        throw new Error('Authentication required for scoring service');
      }
      const token = await user.getIdToken();

      // ✅ Pre-submission analysis
      const figuresWithNames =
        play.figures?.filter((f) => f.name && f.name.trim() !== '') || [];
      const figuresWithoutNames =
        play.figures?.filter((f) => !f.name || f.name.trim() === '') || [];
      const achievedFigures =
        play.figures?.filter((f) => f.achieved || f.achived) || [];

      // ✅ Request Payload Analysis
      const requestPayload = JSON.stringify(play);

      const response = await fetch(`${RANKER_URL}/score`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: requestPayload,
      });

      // ✅ GEÄNDERT: Kein Local Fallback bei API-Fehlern
      if (!response.ok) {
        const errorText = await response.text();

        // ✅ Spezifische Fehler für verschiedene Status Codes
        let errorMessage = 'Scoring service failed';
        switch (response.status) {
          case 401:
            errorMessage = 'Authentication failed - please log in again';
            break;
          case 402:
            errorMessage = 'No active license found';
            break;
          case 403:
            errorMessage = 'Access denied - insufficient permissions';
            break;
          case 404:
            errorMessage = 'Scoring service not found';
            break;
          case 429:
            errorMessage = 'Too many requests - please wait and try again';
            break;
          case 500:
            errorMessage = 'Internal server error - please try again later';
            break;
          case 503:
            errorMessage = 'Scoring service temporarily unavailable';
            break;
          default:
            errorMessage = `Scoring service error (${response.status})`;
        }

        throw new Error(errorMessage);
      }

      const result: any = await response.json();

      // ✅ Enhanced API Response Analysis
      const totalScore = result.play_score?.total_score || 0;
      const isZeroScore = totalScore === 0;
      if (isZeroScore) {
        try {
          const testResult = await this.testScoring(play);
          if (testResult) {
            logger.info('[RankerService] Test scoring breakdown:', {
              testSummary: testResult.summary,
              detailedBreakdown: testResult.test_results?.map((tr: any) => ({
                figureName: tr.figure_name,
                method: tr.method,
                achieved: tr.achieved,
                finalScore: tr.scoring_breakdown?.final_score || 0,
                rawScore: tr.scoring_breakdown?.raw_score || 0,
              })),
            });
          }
        } catch (testError) {
          logger.warn('[RankerService] Test scoring failed:', testError);
        }
      }

      const compatibleResult: RankingResponse = {
        play_score: {
          total_score: result.play_score?.total_score || 0,
          figures_achieved: result.play_score?.figures_achieved || 0,
          figure_count: result.play_score?.figure_count || 0,
        },
        figure_scores:
          result.figure_scores?.map((fs: any) => ({
            computed_score: fs.computed_score || 0,
            achieved: fs.achieved || false,
            figure_name: fs.figure_name,
            figure_name_normalized: fs.figure_name_normalized,
            matrix_found: fs.matrix_found,
            time_range: fs.time_range || { start: 0, end: 0 },
            difficulty_rating: fs.base_score_used || 0,
            scoring_method: fs.scoring_method,
            achievement_multiplier: fs.achievement_multiplier,
          })) || [],
        version: result.version || '1.0.6',
        enhanced_info: result.scoring_info,
      };

      return compatibleResult;
    } catch (error) {
      logger.error('[RankerService] API error - NO FALLBACK:', {
        errorType: error?.constructor?.name,
        errorMessage: error instanceof Error ? error.message : String(error),
        networkError:
          error instanceof Error
            ? error.message?.includes('fetch') ||
              error.message?.includes('network')
            : false,
        authError:
          error instanceof Error
            ? error.message?.includes('auth') ||
              error.message?.includes('token')
            : false,
        localFallbackDisabled: true,
      });

      // ✅ GEÄNDERT: Kein Local Fallback - Error weiterwerfen
      throw error;
    }
  }

  // ✅ KORRIGIERT: Enhanced init test ohne LocalScoring
  private async runInitTest(): Promise<void> {
    try {
      // Health Check
      const isHealthy = await this.isHealthy();

      // Auth Check
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        this.isInitialized = false; // ✅ GEÄNDERT: Nicht initialisiert ohne User
        return;
      }
      // ✅ Test Scoring Info Endpoint
      const scoringInfo = await this.getScoringInfo();
      if (scoringInfo) {
        logger.info('✅ Scoring info loaded:', {
          version: scoringInfo.version,
          matrixLoaded: scoringInfo.scoring_matrix?.loaded,
          availableFigures: scoringInfo.scoring_matrix?.figure_count,
        });
      }

      // ✅ Test Score Calculation (Backend-only)
      const testPlay: Play = {
        figures: [
          {
            name: 'ollie',
            achieved: true,
            i_beg: 100,
            i_end: 200,
            angle: 0,
            stance: 'regular',
          },
          {
            name: '',
            achieved: true,
            i_beg: 300,
            i_end: 400,
            angle: 180,
            stance: 'regular',
          },
        ],
        details: {
          air_time_s: 2.1,
          fly_height_m: 1.5,
          travel_distance_m: 3.2,
          slide_time_s: 1.0,
        },
        id_record: 'backend-only-test',
        id_device: 'test-device',
        title: 'Backend-Only Init Test',
        sections: [],
        steps: [],
      };

      const result = await this.calculateScore(testPlay);
      logger.info('Backend-only RankerService test successful:', {
        totalScore: result.play_score?.total_score,
        figureCount: result.figure_scores?.length,
        hasEnhancedInfo: !!(result as any).enhanced_info,
        version: result.version,
        backendOnly: true,
      });

      this.isInitialized = true;
    } catch (error) {
      logger.error('Backend-only RankerService init test failed:', error);
      this.isInitialized = false;
    }
  }

  /**
   * Get detailed scoring information from the ranker service
   * @returns Promise with scoring rules and matrix info
   */
  async getScoringInfo(): Promise<any> {
    try {
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        return null;
      }

      const token = await user.getIdToken();

      const response = await fetch(`${RANKER_URL}/scoring-info`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        return null;
      }

      const result = await response.json();
      return result;
    } catch (error) {
      return null;
    }
  }

  /**
   * Test scoring logic with detailed breakdown
   * @param play - The play data to test
   * @returns Promise with detailed scoring breakdown
   */
  async testScoring(play: Play): Promise<any> {
    try {
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        return null;
      }

      const token = await user.getIdToken();

      const response = await fetch(`${RANKER_URL}/score/test`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(play),
      });

      if (!response.ok) {
        const errorText = await response.text();
        return null;
      }

      const result = await response.json();

      return result;
    } catch (error) {
      return null;
    }
  }

  /**
   * Check if the ranker service is healthy
   * @returns Promise<boolean> - true if service is healthy
   */
  async isHealthy(): Promise<boolean> {
    try {
      const response = await fetch(`${RANKER_URL}/health`, {
        method: 'GET',
      });
      return response.ok;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get global ranking from the ranker service
   * @param page - Page number (default: 0)
   * @param pageSize - Number of users per page (default: 20)
   * @returns Promise with global ranking data
   */
  async getGlobalRanking(
    page: number = 0,
    pageSize: number = 20
  ): Promise<any[]> {
    try {
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        return [];
      }

      const token = await user.getIdToken();

      const response = await fetch(
        `${RANKER_URL}/ranking/global?page=${page}&page_size=${pageSize}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        return [];
      }

      const result = await response.json();
      return result;
    } catch (error) {
      return [];
    }
  }

  /**
   * Get scoring matrix from the ranker service
   * @returns Promise with scoring matrix data
   */
  async getScoringMatrix(): Promise<any> {
    try {
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        return null;
      }

      const token = await user.getIdToken();

      const response = await fetch(`${RANKER_URL}/scoring-matrix`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        return null;
      }

      const result = await response.json();
      return result;
    } catch (error) {
      return null;
    }
  }

  /**
   * Lädt alle öffentlichen Run-Locations (nur location, lat, lng) vom Backend
   * @returns Promise<Array<{ location: string; latitude: number; longitude: number }>>
   */
  async getAllRunLocations(): Promise<Array<{ location: string; latitude: number; longitude: number }>> {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) throw new Error('Nicht eingeloggt');
      const token = await user.getIdToken();
      const response = await fetch(`${RANKER_URL}/locations/public`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) throw new Error('Fehler beim Abrufen der Run-Locations');
      const result = await response.json();
      // Erwartet: Array von { location, latitude, longitude }
      return result;
    } catch (error) {
      logger.error('[RankerService] Fehler beim Abrufen der Run-Locations:', error);
      return [];
    }
  }

  // Submit user score to backend
  async submitUserScore(play: any, userId: string): Promise<boolean> {
    try {
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        return false;
      }

      const token = await user.getIdToken();

      const response = await fetch(`${RANKER_URL}/submit-score`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          play,
          timestamp: new Date().toISOString(),
        }),
      });

      if (!response.ok) {
        return false;
      }
      return true;
    } catch (error) {
      return false;
    }
  }

  // Get user's best score
  async getUserBestScore(userId: string): Promise<any> {
    try {
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        return null;
      }

      const token = await user.getIdToken();

      const response = await fetch(`${RANKER_URL}/user/${userId}/best-score`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        return null;
      }

      const result = await response.json();
      return result;
    } catch (error) {
      return null;
    }
  }

  // Get custom ranking with filters
  async getCustomRanking(
    filters: any,
    page: number = 0,
    pageSize: number = 20
  ): Promise<any[]> {
    try {
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        return [];
      }

      const token = await user.getIdToken();

      const response = await fetch(
        `${RANKER_URL}/ranking/custom?page=${page}&page_size=${pageSize}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(filters),
        }
      );

      if (!response.ok) {
        return [];
      }

      const result = await response.json();
      return result.users || result || [];
    } catch (error) {
      return [];
    }
  }

  // Get all users
  async getAllUsers(page: number = 0, pageSize: number = 1000): Promise<any[]> {
    try {
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        return [];
      }

      const token = await user.getIdToken();

      const response = await fetch(
        `${RANKER_URL}/users?page=${page}&page_size=${pageSize}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        return [];
      }

      const result = await response.json();
      return result.users || result || [];
    } catch (error) {
      return [];
    }
  }

  /**
   * Lade den Score eines beliebigen Users über das Backend
   * @param userId - Die UID des Users
   * @returns Promise<number|null> - Score oder null bei Fehler
   */
  async getUserScore(userId: string): Promise<number | null> {
    try {
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        return null;
      }

      const token = await user.getIdToken();
      const response = await fetch(`${RANKER_URL}/user/${userId}/score`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        return null;
      }

      const result = await response.json();
      // Erwartet: { score: number }
      if (typeof result.score === 'number') {
        return result.score;
      } else {
        return null;
      }
    } catch (error) {
      return null;
    }
  }

  // Getter für Initialization Status
  get initialized(): boolean {
    return this.isInitialized;
  }

  // Methode, um explizit die Initialisierung zu triggern (z.B. nach Login)
  public async ensureInitialized(): Promise<void> {
    if (!this.isInitialized) {
      await this.runInitTest();
    }
  }
}

// Singleton export
export const rankerService = new RankerService();
